import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import Dropdown from '../receptura/Dropdown';
import usePotvrzeni from '../ui/usePotvrzeni';
import useJeMobil from '../../hooks/useJeMobil';

/**
 * Editor číselníku pro sekce ⚙ Nastavení modulu (norma §1h) — náhrada
 * tabulek ze zrušené `Konfigurace.jsx` (revize Nastavení, 2026-10-12).
 *
 * Seznam řádků (název + meta), klik rozbalí úpravu přímo pod řádkem:
 * popisek nad hodnotou (§1c), vlastní Dropdown místo nativního selectu,
 * Uložit / Zrušit, mazání přes usePotvrzeni. „Přidat" otevře stejný
 * formulář nahoře. Úprava se neukládá na opuštění pole — vždy tlačítkem.
 *
 * pole: [{ klic, nazev, typ: 'text'|'cislo'|'prepinac'|'volba'|'vice',
 *          moznosti?, povinne?, krok?, pripona? (text nebo (form, radek) => text), placeholder?, napoveda?,
 *          zobrazit?: (form) => bool, siroke?: bool }]
 *
 * Data buď načte sám z `nacti` (výchozí `endpoint`), nebo je dostane
 * hotová v `data` (pak po změně volá `onZmena`).
 */
const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';
async function api(url, metoda = 'GET', data) {
    const r = await fetch(url, {
        method: metoda, credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf(), ...(data !== undefined ? { 'Content-Type': 'application/json' } : {}) },
        body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.errors ? Object.values(d.errors).flat()[0] : d?.error || d?.message || (r.status === 403 ? 'Na tohle nemáte oprávnění.' : `Chyba ${r.status}`));
    }
    return r.status === 204 ? null : r.json().catch(() => null);
}

export default function CiselnikEditor({
    endpoint, nacti = null, data = null, onZmena = null,
    pole, titulek = (r) => r.nazev, meta = null, filtr = null, razeni = null,
    vychozi = {}, pripravit = (f) => f, nazevPolozky = 'položku',
    muzePridat = true, muzeMazat = true, upravuje = true, popis = null, prazdne = 'Zatím nic.',
    isDark, barva,
}) {
    const isMobile = useJeMobil();
    const [radky, setRadky] = useState(data);
    const [otevreny, setOtevreny] = useState(null);   // id | 'novy'
    const [form, setForm] = useState({});
    const [radek, setRadek] = useState(null);         // otevřený řádek (pro přípony, napovědy)
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    const obnov = useCallback(async () => {
        if (data !== null) { await onZmena?.(); return; }
        try { const d = await api(nacti || endpoint); setRadky(Array.isArray(d) ? d : (d?.data || [])); }
        catch (e) { setChyba(e.message); setRadky([]); }
    }, [data, onZmena, nacti, endpoint]);
    useEffect(() => { if (data === null) obnov(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (data !== null) setRadky(data); }, [data]);

    const cara = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)';
    const tlumene = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const vstup = {
        width: '100%', height: isMobile ? 44 : 38, borderRadius: 8, padding: '0 10px', outline: 'none', fontSize: 13.5,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };

    const otevri = (r) => {
        setChyba(null);
        const id = r ? r.id : 'novy';
        if (otevreny === id) { setOtevreny(null); return; }
        const zaklad = r ?? vychozi;
        setForm(Object.fromEntries(pole.map((p) => [p.klic,
            p.typ === 'vice' ? (zaklad[p.klic] || [])
                : p.typ === 'prepinac' ? !!(zaklad[p.klic] ?? p.vychozi ?? false)
                    : (zaklad[p.klic] ?? '')])));
        setRadek(r);
        setOtevreny(id);
    };

    const uloz = async () => {
        for (const p of pole) {
            const viditelne = !p.zobrazit || p.zobrazit(form);
            if (viditelne && p.povinne && (form[p.klic] === '' || form[p.klic] == null)) { setChyba(`Vyplň: ${p.nazev}`); return; }
        }
        setUkladam(true); setChyba(null);
        const telo = pripravit(Object.fromEntries(Object.entries({ ...(otevreny === 'novy' ? vychozi : {}), ...form })
            .map(([k, v]) => {
                const p = pole.find((x) => x.klic === k);
                if (p?.typ === 'cislo') return [k, v === '' || v == null ? null : Number(v)];
                if (p?.typ === 'volba') return [k, v === '' ? null : v];
                return [k, v];
            })));
        try {
            await api(otevreny === 'novy' ? endpoint : `${endpoint}/${otevreny}`, otevreny === 'novy' ? 'POST' : 'PUT', telo);
            setOtevreny(null);
            await obnov();
        } catch (e) { setChyba(e.message); }
        setUkladam(false);
    };

    const smaz = async (r) => {
        if (!(await potvrdit(`Smazat ${nazevPolozky} „${titulek(r)}"?`, { potvrdit: 'Smazat' }))) return;
        try { await api(`${endpoint}/${r.id}`, 'DELETE'); setOtevreny(null); await obnov(); }
        catch (e) { setChyba(e.message); }
    };

    const policko = (p) => {
        if (p.zobrazit && !p.zobrazit(form)) return null;
        const zmen = (v) => setForm((f) => ({ ...f, [p.klic]: v }));
        let ovladac;
        if (p.typ === 'prepinac') {
            ovladac = (
                <button type="button" onClick={() => zmen(!form[p.klic])} className="flex items-center gap-2"
                    style={{ minHeight: isMobile ? 44 : 36, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontSize: 13.5 }}>
                    <span style={{ width: 34, height: 20, borderRadius: 10, position: 'relative', background: form[p.klic] ? barva : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'), transition: 'background .15s' }}>
                        <span style={{ position: 'absolute', top: 2, left: form[p.klic] ? 16 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff', transition: 'left .15s' }} />
                    </span>
                    {p.nazev}
                </button>
            );
            return <div key={p.klic} style={{ gridColumn: '1 / -1' }}>{ovladac}{p.napoveda && <span className="block text-xs" style={{ color: tlumene }}>{p.napoveda}</span>}</div>;
        }
        if (p.typ === 'volba') {
            const moznosti = typeof p.moznosti === 'function' ? p.moznosti(form) : p.moznosti;
            ovladac = <Dropdown value={form[p.klic] == null ? '' : String(form[p.klic])} isDark={isDark} accent={barva} height={isMobile ? 44 : 38}
                onChange={(v) => zmen(v)} options={[...(p.povinne ? [] : [{ value: '', label: '—' }]), ...moznosti.map((m) => ({ ...m, value: String(m.value) }))]} />;
        } else if (p.typ === 'vice') {
            const vybrane = form[p.klic] || [];
            ovladac = (
                <div className="flex flex-wrap" style={{ gap: 6 }}>
                    {p.moznosti.map((m) => {
                        const ano = vybrane.includes(m.value);
                        return (
                            <button key={m.value} type="button" onClick={() => zmen(ano ? vybrane.filter((x) => x !== m.value) : [...vybrane, m.value])}
                                style={{ minHeight: isMobile ? 40 : 30, padding: '0 10px', borderRadius: 15, fontSize: 12.5, cursor: 'pointer', background: 'transparent',
                                    color: ano ? 'var(--text-primary)' : tlumene, fontWeight: ano ? 600 : 500,
                                    border: `1px solid ${ano ? barva : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}` }}>
                                {ano ? '✓ ' : ''}{m.label}
                            </button>
                        );
                    })}
                </div>
            );
        } else {
            ovladac = (
                <div className="flex items-center" style={{ gap: 8 }}>
                    <input type={p.typ === 'cislo' ? 'number' : 'text'} step={p.krok} value={form[p.klic] ?? ''} placeholder={p.placeholder}
                        onChange={(e) => zmen(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') uloz(); }} style={vstup} />
                    {p.pripona && <span style={{ fontSize: 12.5, color: tlumene, whiteSpace: 'nowrap' }}>{typeof p.pripona === 'function' ? p.pripona(form, radek || {}) : p.pripona}</span>}
                </div>
            );
        }
        return (
            <label key={p.klic} style={{ display: 'block', gridColumn: p.siroke || isMobile ? '1 / -1' : undefined }}>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{p.nazev}{p.povinne ? '' : ''}</span>
                {ovladac}
                {p.napoveda && <span className="block text-xs" style={{ color: tlumene, marginTop: 3 }}>{p.napoveda}</span>}
            </label>
        );
    };

    const formular = (r) => (
        <div style={{ padding: '12px 4px 14px', borderBottom: `1px solid ${cara}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '12px 16px' }}>
                {pole.map(policko)}
            </div>
            {chyba && <p className="text-sm" style={{ color: '#EF4444', margin: '10px 0 0' }}>{chyba}</p>}
            <div className="flex items-center" style={{ gap: 8, marginTop: 12, flexDirection: isMobile ? 'column-reverse' : 'row' }}>
                {r && muzeMazat && !isMobile && (
                    <button type="button" onClick={() => smaz(r)} className="inline-flex items-center gap-1.5"
                        style={{ height: 36, padding: '0 10px', fontSize: 13, background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer' }}>
                        <Trash2 size={14} /> Smazat
                    </button>
                )}
                <span style={{ flex: 1 }} />
                <button type="button" onClick={() => setOtevreny(null)}
                    style={{ height: isMobile ? 44 : 36, padding: '0 14px', fontSize: 13, borderRadius: 8, cursor: 'pointer', width: isMobile ? '100%' : undefined, background: 'transparent', color: 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                    Zrušit
                </button>
                <button type="button" onClick={uloz} disabled={ukladam}
                    style={{ height: isMobile ? 44 : 36, padding: '0 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer', width: isMobile ? '100%' : undefined, background: barva, color: '#fff', border: 'none', opacity: ukladam ? 0.6 : 1 }}>
                    {ukladam ? 'Ukládám…' : r ? 'Uložit' : 'Přidat'}
                </button>
            </div>
        </div>
    );

    const zobrazene = (radky || []).filter((r) => !filtr || filtr(r));
    if (razeni) zobrazene.sort(razeni);

    return (
        <div>
            {(popis || (muzePridat && upravuje)) && (
                <div className="flex items-start" style={{ gap: 12, marginBottom: 8 }}>
                    <p className="text-xs" style={{ flex: 1, color: tlumene, margin: '6px 0 0' }}>{popis}</p>
                    {muzePridat && upravuje && (
                        <button type="button" onClick={() => otevri(null)} className="inline-flex items-center gap-1.5 shrink-0"
                            style={{ height: 34, padding: '0 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer', background: barva, color: '#fff', border: 'none' }}>
                            <Plus size={14} /> Přidat
                        </button>
                    )}
                </div>
            )}
            {otevreny === 'novy' && formular(null)}
            {radky === null && <p className="text-sm" style={{ color: tlumene }}>Načítám…</p>}
            {radky !== null && zobrazene.length === 0 && otevreny !== 'novy' && <p className="text-sm" style={{ color: tlumene, padding: '8px 0' }}>{prazdne}</p>}
            {zobrazene.map((r) => {
                const otevren = otevreny === r.id;
                const m = meta?.(r);
                return (
                    <div key={r.id}>
                        <button type="button" onClick={() => upravuje && otevri(r)} className="w-full text-left flex items-center"
                            style={{ gap: 10, minHeight: isMobile ? 56 : 44, padding: '6px 4px', border: 'none', borderBottom: otevren ? 'none' : `1px solid ${cara}`,
                                background: otevren ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)') : 'transparent', cursor: upravuje ? 'pointer' : 'default',
                                boxShadow: otevren ? `inset 3px 0 0 ${barva}` : 'none' }}>
                            {upravuje && (otevren ? <ChevronDown size={15} style={{ color: tlumene, flexShrink: 0 }} /> : <ChevronRight size={15} style={{ color: tlumene, flexShrink: 0 }} />)}
                            <span className="flex-1 min-w-0">
                                <span className="block truncate" style={{ fontSize: 13.5, fontWeight: 600, color: r.aktivni === false ? tlumene : 'var(--text-primary)' }}>{titulek(r)}</span>
                                {m && <span className="block truncate" style={{ fontSize: 11.5, color: tlumene }}>{m}</span>}
                            </span>
                        </button>
                        {otevren && formular(r)}
                    </div>
                );
            })}
            {potvrzeni}
        </div>
    );
}
